/**
 * Analytics Plugin
 * Initializes analytics and tracks user identification
 */

import { AnalyticsEvents } from '../types'
import { BackendAnalyticsProvider } from '../providers/backend-analytics.provider'
import { GoogleAnalyticsProvider } from '../providers/google-analytics.provider'
import { analyticsService } from '../services/analytics.service'

export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()
  const analyticsConfig = config.public.analytics

  // Skip if analytics is disabled
  if (!analyticsConfig?.enabled) {
    console.log('[Analytics] Disabled')
    return
  }

  // Initialize service
  analyticsService.initialize(analyticsConfig.debug || false)

  // Store cleanup functions
  const cleanupFunctions: (() => void)[] = []

  // Initialize Google Analytics provider
  if (analyticsConfig.providers?.googleAnalytics?.enabled) {
    const googleProvider = new GoogleAnalyticsProvider(analyticsConfig.debug)

    // Get gtag instance from nuxt-gtag
    const { gtag } = useGtag()
    if (gtag) {
      googleProvider.setGtag(gtag)
      analyticsService.registerProvider('googleAnalytics', googleProvider)
    } else {
      console.warn('[Analytics] gtag not available')
    }
  }

  // Initialize Backend Analytics provider
  if (analyticsConfig.providers?.backendAnalytics?.enabled) {
    const backendProvider = new BackendAnalyticsProvider(analyticsConfig.debug)
    analyticsService.registerProvider('backendAnalytics', backendProvider)
  }

  // ===== Auto-track User Identification =====
  if (analyticsConfig.autoTrack?.userIdentification !== false) {
    try {
      const { store: currentUserStore } = useCurrentUser()

      const stopUserWatch = watch(
        () => currentUserStore.profile,
        (profile) => {
          if (profile) {
            // Handle both CurrentUserProfile (contactId) and CurrentUserProfileV2 (_id)
            const userId = (profile as any)._id || (profile as any).contactId
            const contactId = (profile as any).contactId || (profile as any)._id

            // Identify user (updates context + notifies providers)
            // NOTE: This runs on every page load/refresh to maintain user context
            analyticsService.identify(userId, {
              contactId,
              email: profile.email,
              appName: config.public.appName || 'Unknown',
            })

            // LOGIN_SUCCESS tracking moved to auth layer (saveAuthAfterLogin function)
            // to only track actual logins, not profile loads on refresh
          } else {
            // User logged out - clear user context
            analyticsService.updateContext('userId', undefined)
            analyticsService.updateContext('contactId', undefined)
            analyticsService.updateContext('email', undefined)
          }
        },
        { immediate: true }
      )

      // Store cleanup function
      cleanupFunctions.push(stopUserWatch)
    } catch (error) {
      console.warn('[Analytics] User tracking not available:', error)
    }
  }

  // ===== Auto-track Clicks (via createElementId) =====
  if (analyticsConfig.autoTrack?.clicks !== false) {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement

      // Find closest element with an ID (bubbles up)
      const elementWithId = target.closest('[id]') as HTMLElement

      if (elementWithId?.id) {
        const id = elementWithId.id

        // Parse createElementId pattern: page-context-element
        const parts = id.split('-')

        // Only track if it has at least 3 parts (page-context-element)
        if (parts.length >= 3) {
          analyticsService.track(AnalyticsEvents.ELEMENT_CLICKED, {
            elementId: id,
            elementType: target.tagName.toLowerCase(),
            elementText: target.textContent?.trim().substring(0, 100) || undefined,
            page: parts[0],
            context: parts.slice(1, -1).join('-'),
            element: parts[parts.length - 1],
          })
        }
      }
    }

    document.addEventListener('click', handleClick, { passive: true })

    // Store cleanup function
    cleanupFunctions.push(() => {
      document.removeEventListener('click', handleClick)
    })
  }

  // ===== Auto-track Page Views & Route Context =====
  if (analyticsConfig.autoTrack?.pageViews !== false) {
    const router = useRouter()

    const removeAfterEach = router.afterEach((to, from) => {
      // Update context with current route (enriches ALL subsequent events)
      analyticsService.updateContext('pagePath', to.path)
      analyticsService.updateContext('routeName', to.name as string)

      // Wait for Vue to update document.title, then track page view
      nextTick(() => {
        // Generate fallback title from route name if document.title is empty
        let pageTitle = document.title
        try {
          if (!pageTitle || pageTitle.trim() === '') {
            // Convert route name to readable title: "communities-groups-groupSlug-home" → "Communities Groups Home"
            pageTitle = to.name
              ? String(to.name)
                  .split('-')
                  .filter((part) => !part.includes('Slug') && !part.includes('['))
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(' ')
              : to.path
          }
        } catch (error) {
          console.error('Error generating page title:', error)
          pageTitle = to.path
        }

        analyticsService.track(AnalyticsEvents.PAGE_VIEW, {
          // GA4 standard properties
          pageLocation: window.location.href, // Full URL with domain
          pageTitle, // Document title or generated from route

          // Navigation tracking (from previous page)
          fromPagePath: from.path,
          fromRouteName: from.name as string,
          fromPageLocation: from.fullPath ? window.location.origin + from.fullPath : undefined,
        })
      })
    })

    // Store cleanup function
    cleanupFunctions.push(removeAfterEach)
  }

  // ===== Auto-track Errors =====
  if (analyticsConfig.autoTrack?.errors !== false) {
    // Global error handler
    const handleError = (event: ErrorEvent) => {
      analyticsService.track(AnalyticsEvents.ERROR_OCCURRED, {
        errorMessage: event.message,
        errorType: 'javascript_error',
        filename: event.filename,
        lineno: event.lineno,
      })
    }

    // Unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      analyticsService.track(AnalyticsEvents.ERROR_OCCURRED, {
        errorMessage: event.reason?.message || String(event.reason),
        errorType: 'unhandled_promise_rejection',
      })
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)

    // Store cleanup functions
    cleanupFunctions.push(() => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    })
  }

  // ===== Register Cleanup =====
  let isCleanedUp = false

  const performCleanup = () => {
    if (isCleanedUp) return

    console.log('[Analytics] Cleaning up event listeners and watchers')
    cleanupFunctions.forEach((cleanup) => cleanup())
    isCleanedUp = true
  }

  // Cleanup on page unload
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', performCleanup)
  }

  console.log('[Analytics] Initialized successfully')
})
